import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import ActionsDropdown from 'components/partials/actions-dropdown';
import type { ActionsDropdownOption } from 'components/partials/actions-dropdown/actions-dropdown';
import Toggle from 'components/partials/toggle/toggle';
import Tooltip from 'components/partials/tooltip/tooltip';

import RegimeRow from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/components/regime-row';

import { getRegimeSortableId } from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/use-regimes-sidebar-actions';

import Icons from 'assets/icons';

interface SortableRegimeItemProps {
  regime: GroupRegime;
  actions: ActionsDropdownOption[];
  isDragDisabled: boolean;
  readOnly?: boolean;
}

interface GroupRegime {
  id: string | number;
  name: string;
  sortOrder: number;
  groupId: string | number;
  pell: boolean;
}

interface GroupItem {
  id: string | number;
  name: string;
  enabled: boolean;
  regimes: GroupRegime[];
}

interface GroupRowProps {
  group: GroupItem;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onToggleEnabled: (checked: boolean) => void;
  groupActions: ActionsDropdownOption[];
  regimeActionsFactory: (regime: GroupRegime) => ActionsDropdownOption[];
  isToggleDisabled: boolean;
  isRegimeDragDisabled: (regimeId: string | number) => boolean;
  readOnly?: boolean;
}

const SortableRegimeItem = ({
  regime,
  actions,
  isDragDisabled,
  readOnly,
}: SortableRegimeItemProps): React.JSX.Element => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: getRegimeSortableId(regime.id),
    data: { type: 'regime', entityId: regime.id, groupId: regime.groupId },
    disabled: isDragDisabled,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames(isDragging && 'opacity-30')}
    >
      <RegimeRow
        hideActions={readOnly}
        regime={regime}
        actions={actions}
        dragListeners={listeners as unknown as Record<string, unknown>}
        dragAttributes={attributes as unknown as Record<string, unknown>}
      />
    </div>
  );
};

const GroupRow = ({
  group,
  isExpanded,
  onToggleExpand,
  onToggleEnabled,
  groupActions,
  regimeActionsFactory,
  isToggleDisabled,
  isRegimeDragDisabled,
  readOnly,
}: GroupRowProps): React.JSX.Element => {
  const groupNameRef = useRef<HTMLDivElement | null>(null);
  const [isGroupNameTruncated, setIsGroupNameTruncated] =
    useState<boolean>(false);

  const regimeIds = useMemo(
    () => (group.regimes || []).map((r) => getRegimeSortableId(r.id)),
    [group.regimes]
  );

  useLayoutEffect(() => {
    const element = groupNameRef.current;

    if (element) {
      setIsGroupNameTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [group.name]);

  return (
    <div
      className="flex flex-col gap-1 pb-1"
      data-testid={`group-row-${group.id}`}
    >
      <div className="flex items-center gap-3 p-2 rounded-lg">
        <div className="flex flex-1 items-center gap-2 pr-2 min-w-0">
          <Toggle
            isToggleOn={group.enabled}
            onToggleChange={onToggleEnabled}
            disabled={isToggleDisabled}
            size="sm"
            data-testid={`group-toggle-${group.id}`}
          />

          <Tooltip
            content={group.name}
            enabled={isGroupNameTruncated}
            position="top-start"
          >
            <div
              ref={groupNameRef}
              className="text-sm font-semibold text-gray-700 truncate max-w-36"
            >
              {group.name}
            </div>
          </Tooltip>

          <button
            type="button"
            className="shrink-0 p-0.5"
            onClick={onToggleExpand}
            aria-label={isExpanded ? 'Collapse group' : 'Expand group'}
            data-testid={`group-expand-${group.id}`}
          >
            <Icons.ChevronRight
              customClass={classNames(
                'size-4 text-navy-500 transition-transform duration-200',
                { 'rotate-90': isExpanded }
              )}
            />
          </button>
        </div>

        {!readOnly && (
          <ActionsDropdown
            options={groupActions}
            variant="reserve-text"
            customClass="!h-auto !p-1"
          />
        )}
      </div>

      {isExpanded && group.regimes?.length > 0 && (
        <div className="flex flex-col gap-1">
          <SortableContext
            items={regimeIds}
            strategy={verticalListSortingStrategy}
          >
            {group.regimes.map((regime) => (
              <SortableRegimeItem
                readOnly={readOnly}
                key={regime.id}
                regime={regime}
                actions={regimeActionsFactory(regime)}
                isDragDisabled={isRegimeDragDisabled(regime.id)}
              />
            ))}
          </SortableContext>
        </div>
      )}
    </div>
  );
};

export default GroupRow;
